import {
  FileText, MessagesSquare, CheckCircle2, PenLine, Repeat, ShieldCheck,
  ArrowLeftRight, BookOpen, Layers, Inbox, BarChart3,
  Bell, ScrollText, Users, AlertTriangle, RefreshCw,
  Sparkles, Mail, FileSignature, Search,
  ChevronRight, Building2, KeyRound, Quote, Trophy, Medal, Crown,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { FadeUp, StaggerChildren, StaggerItem, HoverLift } from "@/components/motion";

/* ---------- Heading accent — color only, same heading font ---------- */
export function DisplayAccent({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`text-[hsl(42,90%,50%)] ${className}`}>
      {children}
    </span>
  );
}

/* ============ HERO VISUAL — faux dashboard cluster ============ */
export function HeroVisual() {
  return (
    <div className="relative w-full max-w-5xl mx-auto mt-16 select-none" aria-hidden>
      {/* Glow halo */}
      <div
        className="absolute inset-x-10 -top-10 h-64 rounded-[40px] opacity-40 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 30% 50%, hsl(42,90%,55%) 0%, transparent 60%), radial-gradient(ellipse at 80% 60%, hsl(220,80%,55%) 0%, transparent 60%)" }}
      />
      {/* Main app card */}
      <div className="relative rounded-2xl border border-white/10 bg-[hsl(220,40%,10%)] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-[hsl(220,45%,8%)]">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
          <div className="ml-3 flex-1 max-w-md mx-auto h-6 rounded-md bg-white/5 border border-white/10 px-3 flex items-center text-[10px] text-white/40 font-mono">
            zindeal.com / contracts
          </div>
        </div>
        {/* App body */}
        <div className="grid grid-cols-12 min-h-[420px]">
          {/* Sidebar */}
          <div className="col-span-2 border-r border-white/8 bg-[hsl(220,45%,9%)] p-3 hidden md:block">
            <div className="flex items-center gap-2 mb-5 px-1">
              <div className="w-6 h-6 rounded-md bg-[hsl(42,90%,50%)] flex items-center justify-center">
                <Building2 className="h-3.5 w-3.5 text-[hsl(220,65%,12%)]" />
              </div>
              <span className="text-[11px] font-bold text-white">Zindeal</span>
            </div>
            <div className="space-y-1">
              {[
                { icon: BarChart3, label: "Dashboard" },
                { icon: FileText, label: "Contracts", active: true },
                { icon: ScrollText, label: "Term Sheets" },
                { icon: ArrowLeftRight, label: "Negotiations" },
                { icon: Inbox, label: "Approvals", badge: 3 },
                { icon: RefreshCw, label: "Renewals" },
                { icon: Users, label: "Coaches" },
                { icon: BookOpen, label: "Clauses" },
              ].map((it) => (
                <div
                  key={it.label}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[10px] ${
                    it.active ? "bg-[hsl(42,90%,50%)]/15 text-white border border-[hsl(42,90%,50%)]/25" : "text-white/55"
                  }`}
                >
                  <it.icon className="h-3 w-3 flex-shrink-0" />
                  <span className="flex-1 truncate font-medium">{it.label}</span>
                  {it.badge && (
                    <span className="text-[8px] font-bold bg-[hsl(42,90%,50%)] text-[hsl(220,65%,12%)] rounded-full px-1.5">
                      {it.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          {/* Main */}
          <div className="col-span-12 md:col-span-10 p-5 md:p-6 bg-[hsl(220,40%,10%)]">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">Active contracts</div>
                <div className="text-xl font-extrabold text-white">142 deals · $284M obligations</div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                <div className="h-7 px-2.5 rounded-md border border-white/10 bg-white/5 flex items-center gap-1.5 text-[10px] text-white/60">
                  <Search className="h-3 w-3" /> Search
                </div>
                <div className="h-7 px-2.5 rounded-md bg-[hsl(42,90%,50%)] text-[hsl(220,65%,12%)] flex items-center gap-1.5 text-[10px] font-bold">
                  + New Contract
                </div>
              </div>
            </div>
            {/* KPI strip */}
            <div className="grid grid-cols-4 gap-2.5 mb-5">
              {[
                { label: "Expiring 90d", v: "12", tint: "amber" },
                { label: "In negotiation", v: "8", tint: "blue" },
                { label: "Awaiting sign", v: "5", tint: "violet" },
                { label: "Closed YTD", v: "37", tint: "emerald" },
              ].map((k) => (
                <div key={k.label} className="rounded-lg border border-white/8 bg-white/[0.03] p-2.5">
                  <div className="text-[8.5px] uppercase tracking-wider text-white/35 mb-0.5">{k.label}</div>
                  <div className="text-base font-extrabold text-white">{k.v}</div>
                </div>
              ))}
            </div>
            {/* Contract rows */}
            <div className="space-y-1.5">
              {[
                { coach: "T. Williams", sport: "Football", status: "Awaiting Sign", tint: "bg-violet-500/15 text-violet-300 border-violet-500/25", value: "$4.2M / 4yr" },
                { coach: "S. Park", sport: "Basketball (W)", status: "Negotiating", tint: "bg-blue-500/15 text-blue-300 border-blue-500/25", value: "$1.8M / 3yr" },
                { coach: "R. Mendez", sport: "Soccer", status: "Approved", tint: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25", value: "$680k / 2yr" },
                { coach: "K. Chen", sport: "Volleyball", status: "Renewing", tint: "bg-amber-500/15 text-amber-300 border-amber-500/25", value: "$540k / 3yr" },
              ].map((r) => (
                <div key={r.coach} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-white/8 bg-white/[0.02]">
                  <div className="w-7 h-7 rounded-full bg-[hsl(42,90%,50%)]/20 border border-[hsl(42,90%,50%)]/30 flex items-center justify-center text-[10px] font-bold text-[hsl(42,90%,55%)]">
                    {r.coach.split(" ").map((s) => s[0]).join("")}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold text-white truncate">{r.coach}</div>
                    <div className="text-[9.5px] text-white/40">{r.sport} · {r.value}</div>
                  </div>
                  <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${r.tint}`}>
                    {r.status}
                  </span>
                  <ChevronRight className="h-3 w-3 text-white/30" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating notification card — top right */}
      <div className="hidden lg:block absolute -right-6 top-20 w-72 rounded-xl border border-white/10 bg-[hsl(220,40%,12%)]/95 backdrop-blur-sm p-3 shadow-2xl rotate-[3deg]">
        <div className="flex items-start gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-[hsl(42,90%,50%)]/15 border border-[hsl(42,90%,50%)]/25 flex items-center justify-center flex-shrink-0">
            <Bell className="h-3.5 w-3.5 text-[hsl(42,90%,55%)]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold text-white">Approval requested</div>
            <div className="text-[10px] text-white/50 mt-0.5 leading-snug">
              Williams football contract is ready for review — $4.2M / 4yr.
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <span className="text-[8.5px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                Approve
              </span>
              <span className="text-[8.5px] text-white/40">2m ago</span>
            </div>
          </div>
        </div>
      </div>

      {/* Floating signature card — bottom left */}
      <div className="hidden lg:block absolute -left-6 bottom-12 w-64 rounded-xl border border-white/10 bg-[hsl(220,40%,12%)]/95 backdrop-blur-sm p-3 shadow-2xl -rotate-[3deg]">
        <div className="flex items-center gap-2 mb-2">
          <FileSignature className="h-3.5 w-3.5 text-emerald-400" />
          <span className="text-[10px] font-bold text-white">Contract signed</span>
        </div>
        <div className="text-[10px] text-white/55 leading-snug mb-2">
          R. Mendez countersigned at 2:14 PM ET. PDF archived.
        </div>
        <div className="flex items-center gap-1.5">
          <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full w-full bg-emerald-400" />
          </div>
          <span className="text-[9px] font-bold text-emerald-300">Done</span>
        </div>
      </div>
    </div>
  );
}

/* ============ FOUNDER CREDIBILITY ROW ============ */
export function FounderRow() {
  return (
    <section className="py-20 px-6 bg-[hsl(220,20%,97%)] border-b border-gray-100 relative overflow-hidden">
      <div className="max-w-5xl mx-auto relative">
        <FadeUp>
          <div className="flex flex-col md:flex-row md:items-center gap-8 md:gap-10">
            {/* Founder mark */}
            <div className="flex-shrink-0 mx-auto md:mx-0">
              <div className="relative w-24 h-24 md:w-28 md:h-28">
                <div
                  className="absolute inset-0 rounded-full opacity-30 blur-2xl"
                  style={{ background: "radial-gradient(circle, hsl(42,90%,50%) 0%, transparent 70%)" }}
                />
                <div className="relative w-full h-full rounded-full bg-gradient-to-br from-[hsl(42,90%,50%)] to-[hsl(42,80%,40%)] border-2 border-[hsl(42,90%,55%)]/60 flex items-center justify-center shadow-lg">
                  <span className="font-serif font-bold text-3xl md:text-4xl text-[hsl(220,65%,12%)]">MB</span>
                </div>
              </div>
            </div>

            {/* Founder quote */}
            <div className="flex-1 text-center md:text-left">
              <Quote className="hidden md:inline-block h-6 w-6 text-[hsl(42,90%,50%)] mb-2" />
              <blockquote className="text-lg md:text-xl text-[hsl(220,65%,12%)]/90 leading-relaxed font-medium" style={{ letterSpacing: "-0.005em" }}>
                As the former Chief Human Resources Officer for Stanford Athletics, I was responsible for managing the lifecycle of all coaching contracts across 36 sports. These are by far the most expensive deals of any athletics program across our industry. Zindeal covers the entire coaching contract lifecycle in a single system of record.
                <span className="text-[hsl(42,80%,40%)] font-semibold"> Zindeal is the software I wish I had, and I am humbled and grateful to be able to give back to our industry.</span>
              </blockquote>
              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 justify-center md:justify-start">
                <div className="text-sm font-bold text-[hsl(220,65%,12%)]">Marcus Bryant</div>
                <span className="hidden sm:inline text-gray-300">·</span>
                <div className="text-xs text-gray-600 font-medium">
                  Founder &amp; CEO, Zindeal
                </div>
                <span className="hidden sm:inline text-gray-300">·</span>
                <div className="text-xs text-gray-500">
                  Former CHRO, Stanford Athletics
                </div>
              </div>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

/* ============ FRONT-OFFICE CAPABILITIES (role-neutral) ============ */
const FRONT_OFFICE_PILLARS: { icon: LucideIcon; title: string; desc: string }[] = [
  {
    icon: ScrollText,
    title: "One source of truth for every coach deal",
    desc: "Every draft, every counter, every signed contract in one workspace. Your whole front office pulls from the same source — instead of hunting through inboxes and shared drives.",
  },
  {
    icon: MessagesSquare,
    title: "Live deal collaboration and negotiation",
    desc: "Redline in real time. Counter without losing your place. Loop in counsel and agents inside the deal, not across a hundred-message thread. No more email chains, manual version control, or lost Word docs.",
  },
  {
    icon: Layers,
    title: "Deal templates",
    desc: "Start every deal from a defensible baseline, not a blank page. Reusable frameworks and standard clauses — tuned by sport, role, and level — so your team spends time negotiating, not rebuilding from scratch.",
  },
  {
    icon: Inbox,
    title: "Approvals that route themselves",
    desc: "Push the deal to the right desk by sport, role, or dollar threshold. Approvers decide from their inbox. Every signoff stamped with name, time, and role — automatically.",
  },
  {
    icon: Bell,
    title: "Alerts and deadline management",
    desc: "Renewal windows, option dates, buyout triggers, review cycles — the calendar layer of coach contracts, watching so your staff doesn't have to. Nothing quiet-expires on your watch.",
  },
  {
    icon: BarChart3,
    title: "Live workforce intelligence",
    desc: "Total obligations, buyout exposure, and renewal forecasts rolled up across your entire roster in real time. Pull the numbers that answer real questions.",
  },
];

export function PersonaTabs() {
  return (
    <section className="py-24 px-6 bg-[hsl(220,65%,7%)] relative overflow-hidden border-y border-white/8">
      {/* Dot-grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: "radial-gradient(circle, hsl(42,90%,55%) 1px, transparent 1px)",
          backgroundSize: "28px 28px",
          maskImage: "radial-gradient(ellipse 100% 80% at 50% 50%, black 30%, transparent 75%)",
          WebkitMaskImage: "radial-gradient(ellipse 100% 80% at 50% 50%, black 30%, transparent 75%)",
          opacity: 0.08,
        }}
      />
      {/* Gold radial glow behind headline */}
      <div
        className="absolute inset-x-0 top-0 h-64 pointer-events-none"
        style={{ background: "radial-gradient(ellipse 50% 100% at 50% 0%, hsl(42,90%,50%)/0.10 0%, transparent 70%)" }}
      />
      <div className="max-w-6xl mx-auto relative">
        <FadeUp>
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-[10px] text-white/40 uppercase tracking-[0.18em] font-semibold mb-3">Workforce intelligence for college athletics</p>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white" style={{ letterSpacing: "-0.025em" }}>
              One <DisplayAccent>system of record</DisplayAccent> for every coach deal
            </h2>
          </div>
        </FadeUp>

        <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FRONT_OFFICE_PILLARS.map((p) => (
            <StaggerItem key={p.title}>
              <HoverLift className="h-full">
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-7 h-full flex flex-col backdrop-blur-sm" style={{ boxShadow: "0 20px 40px -20px rgba(0,0,0,0.5)" }}>
                  <div className="w-11 h-11 rounded-xl bg-[hsl(42,90%,50%)]/15 border border-[hsl(42,90%,50%)]/25 flex items-center justify-center mb-4">
                    <p.icon className="h-5 w-5 text-[hsl(42,90%,55%)]" />
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-white" style={{ letterSpacing: "-0.015em" }}>{p.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{p.desc}</p>
                </div>
              </HoverLift>
            </StaggerItem>
          ))}
        </StaggerChildren>
      </div>
    </section>
  );
}


/* ============ LIFECYCLE TIMELINE — connected steps ============ */
const LIFECYCLE = [
  { icon: FileText, title: "Term Sheet", desc: "Build the offer from structured, reusable terms — base, bonuses, buyouts, NIL, perks — instead of forking a Word doc.", color: "hsl(42,90%,55%)" },
  { icon: MessagesSquare, title: "Negotiate", desc: "Every counter from the agent lands in one thread. Compare any two versions side by side, line by line.", color: "hsl(220,80%,65%)" },
  { icon: CheckCircle2, title: "Approve", desc: "Routing rules push the deal to the right desk by sport, role, or dollar threshold — with a clean approval trail.", color: "hsl(280,70%,65%)" },
  { icon: PenLine, title: "E-sign", desc: "Sign in-app or send for signature. The executed PDF lands in the coach's file the same minute.", color: "hsl(158,64%,48%)" },
  { icon: ScrollText, title: "Track", desc: "Live obligations, buyout exposure, insurance status, and the full audit trail per coach.", color: "hsl(200,75%,55%)" },
  { icon: Repeat, title: "Renew", desc: "Every contract pipelined to its renewal date — with 90-, 60-, and 30-day visibility for your front office.", color: "hsl(42,90%,55%)" },
];

export function LifecycleStrip() {
  return (
    <section className="py-24 px-6 bg-[hsl(220,20%,97%)] border-b border-gray-100">
      <div className="max-w-7xl mx-auto">
        <FadeUp>
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-[10px] text-gray-400 uppercase tracking-[0.18em] font-semibold mb-3">Six stages, one workspace</p>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
              From the first call to renewal, <DisplayAccent>one source of truth</DisplayAccent>
            </h2>
            <p className="mt-4 text-gray-500 text-lg">
              Every step a coach deal has to go through — captured, time-stamped, and visible to the people who actually need to see it.
            </p>
          </div>
        </FadeUp>
        <div className="relative">
          {/* connecting line */}
          <div className="hidden md:block absolute left-[10%] right-[10%] top-[42px] h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
          <StaggerChildren className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {LIFECYCLE.map((s, i) => (
              <StaggerItem key={s.title}>
                <div className="text-center relative">
                  <div className="relative inline-flex">
                    <div className="w-20 h-20 rounded-2xl bg-white border-2 border-gray-100 flex items-center justify-center mx-auto relative z-10" style={{ boxShadow: "0 8px 24px -8px rgba(15,28,52,0.15)" }}>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${s.color}1a`, color: s.color }}>
                        <s.icon className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[hsl(220,65%,12%)] text-white text-[10px] font-extrabold flex items-center justify-center z-20 border-2 border-white">
                      {i + 1}
                    </div>
                  </div>
                  <h3 className="font-bold text-sm mt-4 mb-1.5 text-[hsl(220,65%,12%)]">{s.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed max-w-[180px] mx-auto">{s.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </div>
    </section>
  );
}

/* ============ PRODUCT PEEKS — alternating panels ============ */
const PEEKS: { eyebrow: string; title: string; desc: string; bullets: string[]; reverse?: boolean; render: () => React.ReactNode }[] = [
  {
    eyebrow: "Term sheets & agent counters",
    title: "Every counter from the agent, in one place — and diffed",
    desc: "Every term — base, bonuses, buyouts, perks, NIL — captured in a structured offer. When the counter comes back from the agent, Zindeal diffs it line by line so a six-figure change can't slip past you in a Word doc.",
    bullets: [
      "Side-by-side compare across every counter the agent sends back",
      "Inline comments and @mentions on any line of the offer",
      "Full negotiation history archived per coach, ready for review",
    ],
    render: () => (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div className="text-xs font-bold text-[hsl(220,65%,12%)]">Williams · Football · Term Sheet</div>
          <div className="flex items-center gap-1.5 text-[10px]">
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-bold">v3 (counter)</span>
            <span className="text-gray-400">vs</span>
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-bold">v2</span>
          </div>
        </div>
        <div className="grid grid-cols-2 divide-x divide-gray-100 text-[11px]">
          {([
            { l: "Base salary", a: "$1.4M / yr", b: "$1.55M / yr", up: true, same: false },
            { l: "Term", a: "4 years", b: "4 years", up: false, same: true },
            { l: "Signing bonus", a: "$250k", b: "$400k", up: true, same: false },
            { l: "Buyout (Y2)", a: "$2.0M", b: "$1.2M", up: false, same: false },
            { l: "Win bonus (CFP)", a: "$150k", b: "$250k", up: true, same: false },
            { l: "Tickets / suite", a: "10 / yes", b: "10 / yes", up: false, same: true },
          ] as const).flatMap((r) => [
            <div key={`a-${r.l}`} className={`px-4 py-2.5 ${r.same ? "bg-white" : ""}`}>
              <div className="text-[9px] uppercase tracking-wider text-gray-400 mb-0.5">{r.l}</div>
              <div className="font-semibold text-gray-700">{r.a}</div>
            </div>,
            <div key={`b-${r.l}`} className={`px-4 py-2.5 ${r.same ? "bg-white" : "bg-amber-50/60"}`}>
              <div className="text-[9px] uppercase tracking-wider text-gray-400 mb-0.5">{r.l}</div>
              <div className={`font-bold ${r.same ? "text-gray-700" : r.up ? "text-emerald-700" : "text-blue-700"}`}>
                {r.b} {!r.same && (r.up ? "↑" : "↓")}
              </div>
            </div>,
          ])}
        </div>
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-[10px]">
          <span className="text-gray-500">Sarah M. countered · 12 min ago</span>
          <span className="font-bold text-[hsl(220,65%,12%)]">+ 3 deltas · 2 increases · 1 decrease</span>
        </div>
      </div>
    ),
  },
  {
    eyebrow: "Approval routing",
    title: "Approvals that close — with a clean trail",
    desc: "Route the deal in the order your department actually uses, by sport, role, or dollar threshold. Approvers get a one-click decision in their inbox — no login required — and every signoff is stamped with a name, time, and role.",
    reverse: true,
    bullets: [
      "Routed automatically by sport, role, or dollar threshold",
      "One-click approve or request changes from any email client",
      "Every approval state visible to your front office in real time",
    ],
    render: () => (
      <div className="rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center gap-2">
          <Mail className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-xs font-bold text-[hsl(220,65%,12%)]">Approval requested · S. Park basketball</span>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="text-gray-700">
            Hi Rachel — Sarah M. is requesting your approval on the women's basketball term sheet for <span className="font-bold">S. Park</span>.
          </div>
          <div className="rounded-lg border border-gray-100 bg-gray-50/70 p-3 text-[12px] text-gray-600 space-y-1.5">
            <div className="flex justify-between"><span>Total value</span><span className="font-semibold text-[hsl(220,65%,12%)]">$1.8M / 3 yrs</span></div>
            <div className="flex justify-between"><span>Buyout (Y1)</span><span className="font-semibold text-[hsl(220,65%,12%)]">$600k</span></div>
            <div className="flex justify-between"><span>Win incentives</span><span className="font-semibold text-[hsl(220,65%,12%)]">$200k cap</span></div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="flex-1 h-10 rounded-lg bg-[hsl(158,64%,38%)] hover:bg-[hsl(158,64%,32%)] text-white font-bold text-xs transition-colors">
              ✓ Approve
            </button>
            <button type="button" className="flex-1 h-10 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-bold text-xs transition-colors">
              Request changes
            </button>
          </div>
          <div className="text-[10px] text-gray-400 text-center">
            Signed approval link · valid for 7 days · no login required
          </div>
        </div>
      </div>
    ),
  },
];

export function ProductPeeks() {
  return (
    <section className="py-28 px-6 bg-white">
      <div className="max-w-7xl mx-auto space-y-28">
        {PEEKS.map((p, i) => (
          <FadeUp key={p.title} delay={i * 0.05}>
            <div className={`grid lg:grid-cols-2 gap-12 items-center ${p.reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <div className="max-w-xl">
                <p className="text-[10px] text-[hsl(42,90%,45%)] uppercase tracking-[0.18em] font-bold mb-3">
                  {p.eyebrow}
                </p>
                <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[hsl(220,65%,12%)] mb-4" style={{ letterSpacing: "-0.025em" }}>
                  {p.title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed mb-6">
                  {p.desc}
                </p>
                <ul className="space-y-2.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <CheckCircle2 className="h-4 w-4 text-[hsl(158,64%,38%)] flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div
                  className="absolute -inset-6 rounded-3xl opacity-50 blur-2xl pointer-events-none"
                  style={{ background: "radial-gradient(circle at 30% 50%, hsl(42,90%,55%) 0%, transparent 60%), radial-gradient(circle at 70% 80%, hsl(220,80%,65%) 0%, transparent 60%)" }}
                />
                <div className="relative">{p.render()}</div>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
}

export function SeeTheWorkspace() {
  return (
    <section id="peeks" className="py-28 px-6 bg-[hsl(220,20%,97%)] relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ backgroundImage: "linear-gradient(hsl(0,0%,32%) 1px, transparent 1px), linear-gradient(90deg, hsl(0,0%,32%) 1px, transparent 1px)", backgroundSize: "56px 56px" }}
      />
      <div className="max-w-7xl mx-auto relative">
        <FadeUp>
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <p className="text-[10px] text-[hsl(42,90%,45%)] uppercase tracking-[0.18em] font-bold mb-3">Open the doors</p>
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[hsl(220,65%,12%)]" style={{ letterSpacing: "-0.025em" }}>
              A look inside your <DisplayAccent>workspace</DisplayAccent>
            </h2>
            <p className="mt-4 text-gray-600 text-base md:text-lg">
              Two views every athletic department lives in — the language your team has already cleared, and the queue of deals waiting on a signature.
            </p>
          </div>
        </FadeUp>
        <div className="grid lg:grid-cols-2 gap-8">
          <FadeUp delay={0.05}>
            <div className="relative">
              <div className="absolute -top-3 left-6 z-10 px-3 py-1 rounded-full bg-[hsl(42,90%,50%)] text-[hsl(220,65%,12%)] text-[10px] font-bold uppercase tracking-wider shadow-lg">
                Clause Library
              </div>
              <div className="rounded-2xl bg-white border border-white/10 shadow-2xl overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ScrollText className="h-4 w-4 text-[hsl(220,65%,12%)]" />
                    <span className="font-bold text-sm text-[hsl(220,65%,12%)]">Clauses</span>
                    <span className="text-[10px] text-gray-400">· 142 active · 38 locked</span>
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-gray-50 border border-gray-100">
                    <Search className="h-3 w-3 text-gray-400" />
                    <span className="text-[10px] text-gray-400">Search clauses…</span>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {[
                    { name: "Buyout — Year 2 reduction", v: "v3", tags: ["Buyout"], used: 14, locked: false },
                    { name: "NIL collective participation", v: "v2", tags: ["NIL", "Compliance"], used: 9, locked: true },
                    { name: "Termination for cause — DI", v: "v5", tags: ["Termination"], used: 22, locked: false },
                    { name: "Performance bonus — CFP", v: "v2", tags: ["Bonus"], used: 6, locked: false },
                    { name: "Non-compete — conference", v: "v1", tags: ["Non-compete"], used: 4, locked: true },
                  ].map((c) => (
                    <div key={c.name} className="px-5 py-3 hover:bg-gray-50/60 flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-[hsl(220,65%,12%)] truncate">{c.name}</span>
                          {c.locked && <KeyRound className="h-3 w-3 text-amber-600" />}
                        </div>
                        <div className="flex items-center gap-1.5 mt-1">
                          {c.tags.map((t) => (
                            <span key={t} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-semibold">{t}</span>
                          ))}
                          <span className="text-[10px] text-gray-400">· Used in {c.used} contracts</span>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.locked ? "bg-amber-100 text-amber-700 border border-amber-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>{c.v}</span>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 bg-amber-50/60 border-t border-amber-100 text-[11px] text-amber-800 flex items-center gap-2">
                  <KeyRound className="h-3.5 w-3.5" />
                  <span><span className="font-bold">Locked clauses</span> require a compliance unlock request before they can be edited or removed.</span>
                </div>
              </div>
              <p className="mt-4 text-center text-xs text-gray-500 max-w-md mx-auto">
                Your pre-cleared language, versioned and ready to drop in. Every clause carries its history and shows you where it's been used.
              </p>
            </div>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="relative">
              <div className="absolute -top-3 left-6 z-10 px-3 py-1 rounded-full bg-[hsl(158,64%,38%)] text-white text-[10px] font-bold uppercase tracking-wider shadow-lg">
                Approvals queue
              </div>
              <div className="rounded-2xl bg-white border border-white/10 shadow-2xl overflow-hidden">
                <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(220,65%,12%)]" />
                    <span className="font-bold text-sm text-[hsl(220,65%,12%)]">Awaiting your signature</span>
                  </div>
                  <span className="text-[10px] text-gray-400">3 in queue</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {[
                    { coach: "J. Rivera", role: "Head Coach · Football", budget: "approved", people: "pending", compliance: "pending", action: "Approve" },
                    { coach: "M. Chen", role: "Coordinator · M. Basketball", budget: "approved", people: "approved", compliance: "pending", action: "Approve" },
                    { coach: "A. Brooks", role: "Asst. Coach · Soccer", budget: "pending", people: "pending", compliance: "pending", action: "Review" },
                  ].map((row) => (
                    <div key={row.coach} className="px-5 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-[hsl(220,65%,12%)]">Coach {row.coach}</div>
                          <div className="text-[11px] text-gray-500">{row.role}</div>
                        </div>
                        <button className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md bg-[hsl(220,65%,12%)] text-white hover:bg-[hsl(220,55%,22%)]">
                          {row.action}
                        </button>
                      </div>
                      <div className="mt-3 flex items-center gap-1.5 text-[10px]">
                        {(["budget", "people", "compliance"] as const).map((p) => {
                          const status = row[p];
                          const ok = status === "approved";
                          return (
                            <span
                              key={p}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold uppercase tracking-wider ${
                                ok ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"
                              }`}
                            >
                              {ok ? "✓" : "⏳"} {p}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-600 flex items-center gap-2">
                  <PenLine className="h-3.5 w-3.5" />
                  <span><span className="font-bold">Sign in-app</span> or send for emailed signature — signer identity is captured either way.</span>
                </div>
              </div>
              <p className="mt-4 text-center text-xs text-gray-500 max-w-md mx-auto">
                See exactly which approval stage is holding up the deal — and who can unblock it right now.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
